import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh,
  Email,
  Business,
  PriorityHigh,
  Search,
  History,
  MarkunreadMailbox,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import DataTable from "shared/components/Tables/DataTable";

import TableHeader from "./components/TableHeader";
import StatisticsPanel from "./components/StatisticsPanel";
import EmailsModal from "./components/EmailsModal";
import LogsModal from "./components/LogsModal";
import { useReports } from "../../hooks/useReportes";
import { formatLogDate } from "../../model/reportLogs";

const ReportStateCell = ({ value }) => (
  <Chip
    label={value}
    size="small"
    sx={{
      height: 24,
      fontWeight: 600,
      fontSize: 12,
      color: value === "Activo" ? "#166534" : "text.secondary",
      bgcolor: value === "Activo" ? "rgba(22,101,52,0.08)" : "action.hover",
    }}
  />
);
ReportStateCell.propTypes = { value: PropTypes.string };

function TableReportes({ refreshFlag, onRefreshComplete, reportId, reportName, title }) {
  const {
    reports,
    loading,
    error,
    selectedReport,
    showEmailsModal,
    showLogsModal,
    selectedCommerceLogs,
    logs,
    logsLoading,
    logsError,
    notification,
    stats,
    unassignedLogsCount,
    showNotification,
    closeNotification,
    fetchReports,
    viewEmails,
    closeEmailsModal,
    viewCommerceLogs,
    closeLogsModal,
    updateEmail,
    addNewEmail,
    fetchCommerceLogs,
    deleteEmail,
  } = useReports(reportId, reportName);

  const [searchQuery, setSearchQuery] = useState("");
  const initialFetchDone = useRef(false);

  useEffect(() => {
    if (!initialFetchDone.current) {
      fetchReports();
      initialFetchDone.current = true;
    }
  }, [fetchReports]);

  useEffect(() => {
    if (refreshFlag && initialFetchDone.current) {
      fetchReports();
      if (onRefreshComplete) {
        onRefreshComplete();
      }
    }
  }, [refreshFlag, fetchReports, onRefreshComplete]);

  const columns = useMemo(
    () => [
      { Header: "Código", accessor: "codigo", width: "10%" },
      { Header: "Comercio", accessor: "comercioInfo", width: "30%" },
      { Header: "Correos configurados", accessor: "correos", width: "18%", align: "center" },
      { Header: "Historial de envíos", accessor: "correosEnviados", width: "22%" },
      {
        Header: "Estado",
        accessor: "estado",
        width: "10%",
        align: "center",
        Cell: ReportStateCell,
      },
      { Header: "Acciones", accessor: "acciones", width: "10%", align: "center" },
    ],
    []
  );

  const filteredReports = useMemo(() => {
    let result = reports;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((report) => {
        if (!report) return false;
        return (
          (report.comercio || "").toLowerCase().includes(query) ||
          String(report.comercioId || "")
            .toLowerCase()
            .includes(query) ||
          (report.reporte || "").toLowerCase().includes(query)
        );
      });
    }

    return [...result].sort((a, b) => {
      if (a.correosValidos > 0 && b.correosValidos === 0) return -1;
      if (a.correosValidos === 0 && b.correosValidos > 0) return 1;
      return (a.comercio || "").localeCompare(b.comercio || "");
    });
  }, [reports, searchQuery]);

  const rows = useMemo(
    () =>
      filteredReports.map((report) => ({
        codigo: String(report.comercioId || "—"),
        estado: report.estado || "Inactivo",
        comercioInfo: (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {report.comercio || "Sin nombre"}
          </Typography>
        ),
        correos: (
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", gap: 0.5 }}>
            <Tooltip
              title={`${report.correosValidos} con dirección de ${report.totalCorreos} destinatarios configurados`}
              arrow
            >
              <Chip
                icon={<Email sx={{ fontSize: "16px !important", color: "inherit !important" }} />}
                label={`${report.correosValidos} de ${report.totalCorreos}`}
                size="small"
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: report.correosValidos > 0 ? "rgba(22,101,52,0.08)" : "action.hover",
                  color: report.correosValidos > 0 ? "#166534" : "text.secondary",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </Tooltip>
            {report.totalCorreos > report.correosValidos && (
              <Typography sx={{ fontSize: 11, color: "#b45309" }}>
                {report.totalCorreos - report.correosValidos} sin dirección
              </Typography>
            )}
          </Box>
        ),
        correosEnviados:
          report.totalLogs > 0 ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <MarkunreadMailbox sx={{ fontSize: 16, color: "#2563eb" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
                  {report.totalMessages} correos registrados
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                Último: {formatLogDate(report.lastLogDate)}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {report.logsAvailable === false ? "Registro no disponible" : "Sin registros"}
            </Typography>
          ),
        acciones: (
          <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center" }}>
            <Tooltip title="Ver historial de envíos y estados del proveedor" arrow>
              <IconButton
                aria-label={`Ver historial de ${report.comercio}`}
                onClick={() => viewCommerceLogs(report)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563eb",
                  bgcolor: "rgba(37,99,235,0.06)",
                  "&:hover": { bgcolor: "rgba(37,99,235,0.12)" },
                }}
              >
                <History sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ver y gestionar destinatarios del reporte" arrow>
              <IconButton
                aria-label={`Gestionar correos de ${report.comercio}`}
                onClick={() => viewEmails(report)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "1px solid rgba(21,94,102,0.3)",
                  color: "#155e66",
                  bgcolor: "rgba(21,94,102,0.07)",
                  "&:hover": { bgcolor: "rgba(21,94,102,0.14)" },
                }}
              >
                <Email sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      })),
    [filteredReports, viewEmails, viewCommerceLogs]
  );

  return (
    <>
      <TableHeader
        title={title}
        stats={stats}
        loading={loading}
        onRefresh={fetchReports}
        unassignedLogsCount={unassignedLogsCount}
        onViewUnassigned={() =>
          viewCommerceLogs({ comercio: "Sin comercio identificado", comercioId: "__unassigned__" })
        }
      />

      <MDBox px={2} pt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ flexGrow: 1, mr: 2 }}>
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              maxWidth: 400,
            }}
          >
            <MDBox
              sx={{
                p: 1,
                backgroundColor: "background.paper",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Search sx={{ color: "#9e9e9e", mr: 1, fontSize: "1.2rem" }} />
              <ThemedElement
                component="input"
                type="text"
                placeholder="Buscar por comercio, ID o reporte..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  backgroundColor: "transparent",
                  fontSize: "0.875rem",
                  color: "#424242",
                }}
              />
            </MDBox>
          </MDBox>
        </Box>

        <MDBox display="flex" gap={1}>
          <Tooltip title="Actualizar datos" arrow>
            <IconButton
              sx={{
                backgroundColor: "#f5f5f5",
                color: "#424242",
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#e0e0e0" },
              }}
              onClick={fetchReports}
              disabled={loading}
              size="medium"
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>

      <MDBox px={2} pt={1} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          <PriorityHigh
            sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "middle", color: "#2e7d32" }}
          />
          Los comercios con correos válidos aparecen primero
        </MDTypography>

        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          Mostrando {filteredReports.length} de {reports.length} comercios
          {searchQuery && ` (filtrados por "${searchQuery}")`}
        </MDTypography>
      </MDBox>

      <MDBox
        pt={1}
        sx={{
          "& table": { tableLayout: "fixed", width: "100%", minWidth: 1080 },
          "& thead th": { bgcolor: "background.default", py: 1.5, px: 2 },
          "& thead th > div": { opacity: 1, color: "text.primary", fontSize: 11, pr: 2 },
          "& tbody td": { py: 1.25, px: 2, fontSize: 13, height: 62 },
          "& tbody td > div": { width: "100%", whiteSpace: "normal", color: "text.primary" },
          "& tbody td:first-of-type": { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
          "& tbody tr:hover": { bgcolor: "action.hover" },
          "& .MuiTableContainer-root": {
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: "none",
          },
        }}
      >
        {loading && reports.length === 0 ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress sx={{ color: "#0288d1" }} size={60} />
            <MDTypography variant="body2" sx={{ color: "#424242" }} ml={2}>
              Cargando configuraciones de comercios...
            </MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert
              severity="error"
              action={
                <IconButton sx={{ color: "#d32f2f" }} size="small" onClick={fetchReports}>
                  <Refresh />
                </IconButton>
              }
              sx={{ "& .MuiAlert-icon": { color: "#d32f2f" } }}
            >
              {error}
            </Alert>
          </MDBox>
        ) : filteredReports.length === 0 ? (
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            p={3}
          >
            <Business sx={{ fontSize: 60, color: "#616161", mb: 2 }} />
            <MDTypography variant="h6" sx={{ color: "#424242" }} gutterBottom>
              {searchQuery ? "No se encontraron resultados" : "No hay comercios configurados"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#757575" }}>
              {searchQuery
                ? "Intenta con otros términos de búsqueda"
                : `Configura comercios en el reporte ${reportName}`}
            </MDTypography>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            autoResetPage={false}
            paginationResetKey={searchQuery}
            isSorted={true}
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25, 50, 100] }}
            showTotalEntries={true}
            pagination={{ variant: "outlined", color: "info" }}
            canSearch={false}
            noEndBorder
          />
        )}
      </MDBox>

      <StatisticsPanel stats={stats} reportName={reportName} />

      <EmailsModal
        open={showEmailsModal}
        onClose={closeEmailsModal}
        report={selectedReport}
        onUpdateEmail={updateEmail}
        onAddNewEmail={addNewEmail}
        onDeleteEmail={deleteEmail}
        onRefresh={fetchReports}
      />

      <LogsModal
        open={showLogsModal}
        onClose={closeLogsModal}
        commerce={selectedCommerceLogs}
        logs={logs}
        loading={logsLoading}
        error={logsError}
        onRefresh={fetchCommerceLogs}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 400 }}
        >
          <Box>
            <MDTypography variant="body2" fontWeight="medium">
              {notification.message}
            </MDTypography>
            {notification.details && (
              <MDTypography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.9 }}>
                {notification.details}
              </MDTypography>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}

TableReportes.propTypes = {
  reportId: PropTypes.number,
  reportName: PropTypes.string,
  title: PropTypes.string,
  refreshFlag: PropTypes.bool,
  onRefreshComplete: PropTypes.func,
};

TableReportes.defaultProps = {
  reportId: 1,
  reportName: "RejectedStep",
  title: "Pasos rechazados",
  refreshFlag: false,
  onRefreshComplete: () => {},
};

export default TableReportes;
